import styled from 'styled-components';

interface TitleProps {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  color?: string;
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  align?: 'left' | 'center' | 'right';
  transform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  margin?: string;
  padding?: string;
  truncate?: boolean;
}

const getTitleStyles = (level: number) => {
  switch (level) {
    case 1:
      return {
        fontSize: '2.25rem',
        lineHeight: '2.5rem',
        fontWeight: '700'
      };
    case 2:
      return {
        fontSize: '1.875rem',
        lineHeight: '2.25rem',
        fontWeight: '600'
      };
    case 3:
      return {
        fontSize: '1.5rem',
        lineHeight: '2rem',
        fontWeight: '600'
      };
    case 4:
      return {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        fontWeight: '600'
      };
    case 5:
      return {
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
        fontWeight: '600'
      };
    case 6:
      return {
        fontSize: '1rem',
        lineHeight: '1.5rem',
        fontWeight: '600'
      };
    default:
      return {
        fontSize: '1.5rem',
        lineHeight: '2rem',
        fontWeight: '600'
      };
  }
};

const getFontWeight = (weight: string) => {
  switch (weight) {
    case 'normal': return '400';
    case 'medium': return '500';
    case 'semibold': return '600';
    case 'bold': return '700';
    default: return '600';
  }
};

export const Title = styled.h1.withConfig({
  shouldForwardProp: (prop) => !['level', 'color', 'weight', 'align', 'transform', 'truncate'].includes(prop)
})<TitleProps>`
  ${props => {
    const styles = getTitleStyles(props.level || 3);
    return Object.entries(styles).map(([key, value]) => 
      `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
    ).join(' ');
  }}
  
  color: ${props => props.color || '#111827'};
  font-weight: ${props => getFontWeight(props.weight || 'semibold')};
  text-align: ${props => props.align || 'left'};
  text-transform: ${props => props.transform || 'none'};
  margin: ${props => props.margin || '0 0 1rem 0'};
  padding: ${props => props.padding || '0'};
  
  ${props => props.truncate && `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

// Specialized title variants
export const PageTitle = styled(Title).attrs({ level: 1 })`
  margin-bottom: 2rem;
`;

export const SectionTitle = styled(Title).attrs({ level: 2 })`
  margin-bottom: 1.5rem;
`;

export const SubsectionTitle = styled(Title).attrs({ level: 3 })`
  margin-bottom: 1rem;
`;

export const CardTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 0.75rem;
`;

export const FormTitle = styled(Title).attrs({ level: 3 })`
  margin-bottom: 1.5rem;
  color: #374151;
`;

export const ModalTitle = styled(Title).attrs({ level: 3 })`
  margin: 0;
  color: #111827;
`;

// Semantic title variants
export const ErrorTitle = styled(Title)`
  color: #ef4444;
`;

export const SuccessTitle = styled(Title)`
  color: #10b981;
`;

export const WarningTitle = styled(Title)`
  color: #f59e0b;
`;

export const MutedTitle = styled(Title)`
  color: #6b7280;
`;